import { useEffect, useRef, type CSSProperties } from 'react'
import { observer } from 'mobx-react-lite'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'

import { STEPS, type Step } from './howSteps'
import { scrollToSlide, useHowRailStore } from './HowRailStore'
import styles from './HowRail.module.scss'

/**
 * "How it works" on a phone: one step per slide, swiped sideways, dots under it.
 *
 * The desktop's six alternating rows took ~2 800px here, 3.3 screens, and their miniatures came out at
 * ×1.04 with 5.5–8.5px labels nobody could read (mobile audit 2026-09-28, f1212). A slide holds the
 * miniature at ×1.5, cropped to the part of it that carries the step (the `pan` of each step in
 * howSteps.ts), and the step's words under it at body size; the whole block is one screen.
 *
 * Rendered only under the phone breakpoint (HowItWorks.tsx decides), so the desktop rows, their reveal
 * and their DOM are exactly what they were.
 *
 * The dots are buttons named by the step's own title — no new string, and a screen reader hears
 * "We score across 100+ signals", which says more than "3 of 6".
 */
export const HowRail = observer(() => {
  const { t } = useTranslation()
  const store = useHowRailStore({ count: STEPS.length })
  const railRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const rail = railRef.current
    return rail ? store.attach(rail) : undefined
  }, [store])

  /* Ключ собирается из step.key, поэтому проверка i18n его не видит — ветки перечислены в
     landing.how.steps: connect, filters, score, shortlist, sequence, close. */
  const line = (step: Step, part: string) => t(`landing.how.steps.${step.key}.${part}`)

  return (
    <div className={styles.root}>
      <div ref={railRef} className={styles.rail}>
        {STEPS.map((step, index) => (
          <article key={step.n} className={styles.slide} aria-label={line(step, 'title')}>
            <div className={styles.frame} style={{ '--pan-x': step.pan.x, '--pan-y': step.pan.y } as CSSProperties}>
              <div className={styles.visual}>
                <step.Visual playing={store.isPlaying(index)} />
              </div>
            </div>
            <div className={styles.body}>
              <span className={styles.n}>
                {step.n}
                {step.soon ? <span className={styles.soon}>{t('landing.how.soon')}</span> : null}
              </span>
              <h3 className={styles.title}>{line(step, 'title')}</h3>
              <p className={styles.text}>{line(step, 'text')}</p>
            </div>
          </article>
        ))}
      </div>

      <div className={styles.dots} role="group" aria-label={t('landing.how.kicker')}>
        {STEPS.map((step, index) => (
          <button
            key={step.n}
            type="button"
            className={cn(styles.dot, { [styles.dotActive as string]: index === store.active })}
            aria-label={line(step, 'title')}
            aria-current={index === store.active ? 'step' : undefined}
            onClick={() => railRef.current && scrollToSlide(railRef.current, index)}
          />
        ))}
      </div>
    </div>
  )
})
